import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import moment from "moment";
import CustomerMainLayout from "../CustomerMainLayout";
import ENVIRONMENT from "../../../../environments";
import FileConstants from "../../../../FileConstants";
import _ from "lodash"; // Import the entire lodash library
import * as Utilities from "../../Utilities";
import TwoDImagesContext from "../../ContextFiles/TwoDImagesContext";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import TwoDImagesHeader from "./TwoDImagesHeader";
import TwoDImagesTabs from "./TwoDImagesTabs";
import SubscriptionUpgradeManager from "../SubscriptionUpgradeManager/SubscriptionUpgradeManager";
import UpscaleImageModal from "../AIEditorModalViewer/UpscaleImageModal";
import "./TwoDImages.scss";
import {
  GET_IMAGES,
  PORTAL_LINK,
  getBaseURL,
} from "../../../../environments/env";
import { useQuery } from "react-query";
import pako from "pako";
import { message } from "antd";

const COMPANY_ID = localStorage.getItem("company_id");
const CUSTOMER_USERNAME = localStorage.getItem("username");
const MANAGED_CUSTOMER_USERNAME = localStorage.getItem(
  "managed_customer_username"
);
const IS_MS_PROVIDER = FileConstants.isMSProvider;
const MANAGED_COMPANY_ID = FileConstants.MANAGED_COMPANY_ID;
const MANAGED_SHARED_ENTITIES = localStorage.getItem("managed_shared_entites");
const SHARED_ENTITIES = localStorage.getItem("shared_entities");

const initializeStateFromQueryParams = () => {
  const query = new URLSearchParams(window.location.search);
  const searchTerm = query.get("searchTerm") || "";
  const project = query.get("project") || "";
  const startDate = query.get("startDate") || "";
  const endDate = query.get("endDate") || "";
  const types = JSON.parse(query.get("types") || "[]");
  const isAiCustomer = localStorage.getItem('is_ai_customer') === 'true';
  const type = query.get("type") || (isAiCustomer ? "ai" : "all");
  const engine = query.get("engine") || "";
  const username = query.get("username") || "";
  const bulkRequestId = query.get("bulkRequestId") || "";

  return {
    searchTerm,
    startDate,
    endDate,
    types,
    project,
    type,
    engine,
    username,
    bulkRequestId,
  };
};

const initialResolutionState = () => {
  const query = new URLSearchParams(window.location.search);
  return query.get("resolution") || "all";
};

const initialTabResolutions = () => {
  const query = new URLSearchParams(window.location.search);
  const tabResolutions = query.get("tabResolutions");
  if (tabResolutions) {
    try {
      return JSON.parse(tabResolutions);
    } catch (e) {
      console.error("Error parsing tabResolutions from URL:", e);
    }
  }
  return {};
};

const TwoDImagesPage = () => {
  const [resolution, setResolution] = useState(initialResolutionState());
  const [tabResolutions, setTabResolutions] = useState(initialTabResolutions());
  const [projects, setProjects] = useState([]);

  // --- Subscription/credits state ---
  const [subscriptionUpgradeManagerModel, setSubscriptionUpgradeManagerModel] = useState(false);
  const [currentRequestType, setCurrentRequestType] = useState('ai_image_1k');
  const [cardStatus, setCardStatus] = useState(0);
  const [imageRequestStatus, setImageRequestStatus] = useState('loading');
  const [upscaleModalOpen, setUpscaleModalOpen] = useState(false);
  const [upscaleRenderId, setUpscaleRenderId] = useState(null);

  // AI Image Stack State Management (similar to folder state in CustomerProducts)
  const [stackState, setStackState] = useState(false);
  const [stackImages, setStackImages] = useState([]);
  const [selectedStackParentId, setSelectedStackParentId] = useState(null);

  // Track new_image_id from URL params for showing temporary loading object
  const [showNewImageLoader, setShowNewImageLoader] = useState(() => {
    const url = new URL(window.location.href);
    return url.searchParams.get('new_image_id') || null;
  });

  const [newImageSrc, setNewImageSrc] = useState(() => {
    const url = new URL(window.location.href);
    return url.searchParams.get('image_src') || null;
  });

  const [formState, setFormState] = useState({
    ...initializeStateFromQueryParams(),
  });

  const [Type, setType] = useState(initializeStateFromQueryParams().type);

  // State to store fetched prompts (prompt_text, flux_prompt, search_keywords) for each render_id
  const [promptsData, setPromptsData] = useState({});
  const [promptsLoading, setPromptsLoading] = useState(false);

  // Bulk request filter state
  const [completedBulkRequests, setCompletedBulkRequests] = useState([]);

  // Fetch bulk requests for the user
  useEffect(() => {
    const fetchBulkRequests = async () => {
      try {
        const payload = {
          action: 'get_user_bulk_request',
          username: CUSTOMER_USERNAME,
          company_id: COMPANY_ID
        };

        const res = await axios.post(ENVIRONMENT.AI_BULK_JOB_CONTROLLER, payload);
        if (res.data?.data && Array.isArray(res.data?.data)) {
          const completed = res.data.data
            .filter(request => request.status === 'completed')
            .map(request => ({
              id: request.id,
              name: request.bulk_upload_name || `Request ${request.id}`
            }));
          setCompletedBulkRequests(completed);
        }
      } catch (error) {
        console.error('Error fetching bulk requests:', error);
        setCompletedBulkRequests([]);
      }
    };

    fetchBulkRequests();
  }, []);

  useEffect(() => {
    setFormState({
      ...formState,
      type: Type,
    });
  }, [Type]);

  // FETCHER FUNCTION
  const twoDImagesFetcher = useCallback((payload) => {
    return axios
      .post(GET_IMAGES, payload, {
        responseType: "json",
        headers: {
          "Accept-Encoding": "gzip",
        },
      })
      .then((res) => {
        const encodedData = res.data["body"];
        const compressedData = new Uint8Array(
          atob(encodedData)
            .split("")
            .map((c) => c.charCodeAt(0))
        );
        const decompressedData = pako.inflate(compressedData);
        let jsonData = JSON.parse(
          new TextDecoder("utf-8").decode(decompressedData)
        );

        // Handle paginated response format
        if (jsonData && jsonData.data && Array.isArray(jsonData.data)) {
          // Paginated response: { data: [...], pagination: {...} }
          const processedData = jsonData.data.map((image) => {
            if (image.image_type == 'ai' && image.props) {
              image['render_props'] = image.props.filter(item => item.product !== true)
              image['props_image_uris'] = image.props.map((prop) => {
                return prop.image_uri;
              });
            }
            return image;
          });
          return {
            data: processedData,
            pagination: jsonData.pagination || null
          };
        } else if (Array.isArray(jsonData)) {
          // Legacy non-paginated response: [...]
          const processedData = jsonData.map((image) => {
            if (image.image_type == 'ai' && image.props) {
              image['render_props'] = image.props.filter(item => item.product !== true)
              image['props_image_uris'] = image.props.map((prop) => {
                return prop.image_uri;
              });
            }
            return image;
          });
          return processedData;
        }
        console.log('Fetched images:', jsonData);
        return jsonData;
      })
      .catch((error) => console.error(error));
  }, []);

  const favoritesFetcher = useCallback((payload) => {
    return axios
      .post(ENVIRONMENT.FAVOURITES_CONTROLLER, payload)
      .then((res) => res.data);
  }, []);

  // Background fetcher for AI image prompts
  const fetchAIImagePrompts = useCallback((renderIds) => {
    if (!renderIds || renderIds.length === 0) return Promise.resolve({});

    const payload = {
      username: IS_MS_PROVIDER && MANAGED_CUSTOMER_USERNAME
        ? MANAGED_CUSTOMER_USERNAME
        : CUSTOMER_USERNAME,
      company_id: IS_MS_PROVIDER && MANAGED_COMPANY_ID ? MANAGED_COMPANY_ID : COMPANY_ID,
      render_ids: renderIds,
      action: "ai_image_prompts",
      image_type: "ai_image_prompts"
    };

    return axios
      .post(GET_IMAGES, payload, {
        responseType: "json",
        headers: {
          "Accept-Encoding": "gzip",
        },
      })
      .then((res) => {
        const encodedData = res.data["body"];
        const compressedData = new Uint8Array(
          atob(encodedData)
            .split("")
            .map((c) => c.charCodeAt(0))
        );
        const decompressedData = pako.inflate(compressedData);
        const jsonData = JSON.parse(
          new TextDecoder("utf-8").decode(decompressedData)
        );

        // console.log('Fetched AI image prompts:', jsonData);

        // Convert array to object with render_id as key
        const promptsMap = {};
        if (Array.isArray(jsonData)) {
          jsonData.forEach(item => {
            if (item.id) {
              promptsMap[item.id] = {
                prompt_text: item.prompt_text,
                search_keywords: item.search_keywords
              };
            }
          });
        }
        return promptsMap;
      })
      .catch((error) => {
        console.error('Error fetching AI image prompts:', error);
        return {};
      });
  }, [CUSTOMER_USERNAME, COMPANY_ID, IS_MS_PROVIDER, MANAGED_CUSTOMER_USERNAME]);

  const aiGeneratedRenders = useCallback((payload) => {
    return axios.post(ENVIRONMENT.AI_GENERATED_IMAGES, payload).then((res) => {
      console.log(res.data);

      return res.data.map((img) => {
        return {
          ...img,
          image_type: "ai",
          is360: img.threesixty_frames !== undefined,
          entity_id: img.product_id,
          platform: img.platform,
          entity_type: "product",
          image_name: img.id.toString() + ".jpg",
          threesixty_frames: img.threesixty_frames !== undefined ? img.threesixty_frames.data : null,
        };
      });
    });
  }, []);

  // Pagination state for tabs that support pagination
  const [paginationState, setPaginationState] = useState({
    ai: { loaded: 0, loading: false, hasMore: true, backgroundLoading: false, apiCalled: false },
    final_images: { loaded: 0, loading: false, hasMore: true, backgroundLoading: false, apiCalled: false },
    ai_video: { loaded: 0, loading: false, hasMore: true, backgroundLoading: false, apiCalled: false },
  });

  const allImagesPayload = useMemo(
    () => ({
      username:
        IS_MS_PROVIDER && MANAGED_CUSTOMER_USERNAME
          ? MANAGED_CUSTOMER_USERNAME
          : CUSTOMER_USERNAME,
      company_id:
        IS_MS_PROVIDER && MANAGED_COMPANY_ID ? MANAGED_COMPANY_ID : COMPANY_ID,
      shared_entities:
        IS_MS_PROVIDER && MANAGED_CUSTOMER_USERNAME
          ? MANAGED_SHARED_ENTITIES
          : SHARED_ENTITIES,
      image_type: "all",
    }),
    [CUSTOMER_USERNAME, COMPANY_ID, IS_MS_PROVIDER, MANAGED_CUSTOMER_USERNAME]
  );

  // Paginated payloads for tabs that support pagination
  const getPaginatedPayload = useCallback((tabType, limit = 20, offset = 0) => {
    const actionMap = {
      ai: 'previews_paginated',
      final_images: 'final_images_paginated',
      ai_video: 'videos_paginated',
    };

    const action = actionMap[tabType];
    if (!action) return null;

    return {
      username:
        IS_MS_PROVIDER && MANAGED_CUSTOMER_USERNAME
          ? MANAGED_CUSTOMER_USERNAME
          : CUSTOMER_USERNAME,
      company_id:
        IS_MS_PROVIDER && MANAGED_COMPANY_ID ? MANAGED_COMPANY_ID : COMPANY_ID,
      shared_entities:
        IS_MS_PROVIDER && MANAGED_CUSTOMER_USERNAME
          ? MANAGED_SHARED_ENTITIES
          : SHARED_ENTITIES,
      image_type: action,
      limit,
      offset,
    };
  }, [CUSTOMER_USERNAME, COMPANY_ID, IS_MS_PROVIDER, MANAGED_CUSTOMER_USERNAME]);

  const aiGeneratedImagesPayload = useMemo(
    () => ({
      customer_username: CUSTOMER_USERNAME,
    }),
    [CUSTOMER_USERNAME]
  );
  const payloadFavorites = {
    action: "get",
    favorited_by: CUSTOMER_USERNAME,
  };

  const {
    data: favoriteImages,
    refetch: favoriteImagesRefetch,
    isLoading: imagesFavoriteLoading,
  } = useQuery(
    [`${ENVIRONMENT.FAVOURITES_CONTROLLER}`, payloadFavorites],
    () => favoritesFetcher(payloadFavorites),
    {
      enabled: !!payloadFavorites,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  // Store paginated images separately
  const [paginatedImages, setPaginatedImages] = useState({
    ai: [],
    final_images: [],
    ai_video: [],
  });

  // Load paginated images for a specific tab
  const loadPaginatedImages = useCallback(async (tabType, limit = 20, offset = 0) => {
    const payload = getPaginatedPayload(tabType, limit, offset);
    if (!payload) return null;

    try {
      const result = await twoDImagesFetcher(payload);
      // Handle both paginated and non-paginated responses
      if (result && result.data && Array.isArray(result.data)) {
        return {
          images: result.data,
          pagination: result.pagination,
        };
      } else if (Array.isArray(result)) {
        return {
          images: result,
          pagination: null,
        };
      }
      return { images: [], pagination: null };
    } catch (error) {
      console.error(`Error loading paginated images for ${tabType}:`, error);
      return { images: [], pagination: null };
    }
  }, [getPaginatedPayload, twoDImagesFetcher]);

  // Load first 50 images, then load remaining in batches of 500
  const loadTabImages = useCallback(async (tabType, reset = false) => {
    if (!['ai', 'final_images', 'ai_video'].includes(tabType)) return;

    // Prevent duplicate calls if already loading (unless reset is true)
    if (!reset && paginationState[tabType]?.loading) {
      return;
    }

    // Prevent duplicate calls if already background loading
    if (!reset && paginationState[tabType]?.backgroundLoading) {
      return;
    }

    if (reset) {
      setPaginatedImages(prev => ({
        ...prev,
        [tabType]: []
      }));
    }

    setPaginationState(prev => ({
      ...prev,
      [tabType]: { ...prev[tabType], loading: true, loaded: 0, apiCalled: true }
    }));

    // Local state for this specific tab's loading session
    const allImages = [];
    let currentOffset = 0;
    const initialPageSize = 200; // Load first 50 images in first call
    const backgroundBatchSize = 7000; // Load remaining in batches of 500

    // ========== PHASE 1: Load first 50 images ==========
    const firstResult = await loadPaginatedImages(tabType, initialPageSize, currentOffset);
    let tabHasMore = false; // Tab-specific flag for whether more data exists

    if (firstResult && firstResult.images && firstResult.images.length > 0) {
      allImages.push(...firstResult.images);
      currentOffset += firstResult.images.length;

      // Determine if there are more images for THIS specific tab
      if (firstResult.pagination) {
        tabHasMore = firstResult.pagination.returned_count >= initialPageSize;
      } else {
        tabHasMore = firstResult.images.length >= initialPageSize;
      }
    } else {
      tabHasMore = false;
    }

    // Update state with initial images
    setPaginatedImages(prev => ({
      ...prev,
      [tabType]: [...allImages]
    }));

    setPaginationState(prev => ({
      ...prev,
      [tabType]: {
        ...prev[tabType],
        loading: false,
        loaded: allImages.length,
        hasMore: tabHasMore,
        backgroundLoading: tabHasMore
      }
    }));

    // ========== PHASE 2: Load remaining images in background ==========
    if (tabHasMore) {
      // Continue loading in batches of 500
      while (tabHasMore) {
        try {
          const result = await loadPaginatedImages(tabType, backgroundBatchSize, currentOffset);

          if (!result) {
            tabHasMore = false;
            break;
          }

          if (result.images && result.images.length > 0) {
            allImages.push(...result.images);
            currentOffset += result.images.length;

            // Update state with new batch
            setPaginationState(prev => ({
              ...prev,
              [tabType]: { ...prev[tabType], loaded: allImages.length }
            }));

            setPaginatedImages(prev => ({
              ...prev,
              [tabType]: [...allImages]
            }));

            // Check if THIS tab has more images
            if (result.pagination) {
              tabHasMore = result.pagination.returned_count >= backgroundBatchSize;
            } else {
              tabHasMore = result.images.length >= backgroundBatchSize;
            }
          } else {
            tabHasMore = false;
          }
        } catch (error) {
          console.error(`[${tabType}] Error in background loading:`, error);
          tabHasMore = false;
          break;
        }
      }

      setPaginationState(prev => ({
        ...prev,
        [tabType]: {
          ...prev[tabType],
          hasMore: false,
          backgroundLoading: false
        }
      }));
    }
  }, [loadPaginatedImages, paginationState]);

  // Preload all three paginated tabs on component mount
  useEffect(() => {
    const tabsToLoad = ['ai', 'final_images', 'ai_video'];
    tabsToLoad.forEach((tabType) => {
      // Start loading in parallel (don't await) - loadTabImages will check if already called
      if (!paginationState[tabType].apiCalled) {
        loadTabImages(tabType, false);
      }
    });
  }, []); // Only run once on mount

  // Load tab images when Type changes to a paginated tab (fallback)
  useEffect(() => {
    if (Type && ['ai', 'final_images', 'ai_video'].includes(Type)) {
      // Only load if we don't have images yet for this tab and API hasn't been called
      if (paginatedImages[Type].length === 0 && !paginationState[Type].apiCalled) {
        loadTabImages(Type, false);
      }
    }
  }, [Type, loadTabImages, paginatedImages, paginationState]);

  // Check if current user is an AI customer
  const isAiCustomer = localStorage.getItem('is_ai_customer') === 'true';

  const {
    data: twoDImagesAll,
    refetch: twoDAllImagesRefetch,
    isLoading: imagesAllLoading,
  } = useQuery(
    [`${GET_IMAGES}/initial`, allImagesPayload],
    () => twoDImagesFetcher(allImagesPayload),
    {
      // Disable for paginated tabs, and also for favourites tab when AI customer (use combined paginated data instead)
      enabled: !!allImagesPayload && !['ai', 'final_images', 'ai_video'].includes(Type) && !(Type === 'favourites' && isAiCustomer),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const refetch = () => {
    const promises = [favoriteImagesRefetch()];

    // Only refetch all images if not on a paginated tab
    if (!Type || !['ai', 'final_images', 'ai_video'].includes(Type)) {
      promises.push(twoDAllImagesRefetch());
    } else {
      // Reload paginated images for current tab (reset existing images)
      promises.push(loadTabImages(Type, true));
    }

    return Promise.all(promises);
  };

  // Combine paginated and non-paginated images based on current tab
  const twoDImages = useMemo(() => {
    if (Type && ['ai', 'final_images', 'ai_video'].includes(Type)) {
      return paginatedImages[Type] || [];
    }
    // For AI customers on favourites tab, combine all paginated data
    if (Type === 'favourites' && isAiCustomer) {
      return [
        ...paginatedImages.ai,
        ...paginatedImages.final_images,
        ...paginatedImages.ai_video
      ];
    }
    return twoDImagesAll || [];
  }, [Type, twoDImagesAll, paginatedImages, isAiCustomer]);

  // Calculate favorites count from combined paginated images using favorite mapping (stable across tab changes)
  const favouritesCount = useMemo(() => {
    if (isAiCustomer) {
      // Build the same favoriteImagesMapping as used in twoDImagesWithFavoriteFlag
      const favoriteImagesMapping = favoriteImages?.reduce(
        (acc, obj) => {
          const key = obj?.image_type === 'ai_video'
            ? `${obj?.entity_id}`
            : `${obj?.entity_id}_${obj.image_name}`;
          return { ...acc, [key]: obj };
        },
        {}
      ) || {};

      // Combine all paginated images
      const allPaginatedImages = [
        ...paginatedImages.ai,
        ...paginatedImages.final_images,
        ...paginatedImages.ai_video
      ];

      // Count images that match the favorite mapping using the same key logic
      return allPaginatedImages.filter(obj => {
        let key;
        if (obj.image_type === "ai") {
          key = `${obj.id}_${obj.image_name}`;
        } else if (obj.image_type === "ai_video") {
          key = obj.id;
        } else {
          // Default key format for other types
          let filename = obj.image_name;
          key = `${obj.entity_id}_${filename}`;
        }
        return favoriteImagesMapping[key] ? true : false;
      }).length;
    }
    // For non-AI customers, return null to use the default calculation
    return null;
  }, [paginatedImages, favoriteImages, isAiCustomer]);

  // Fetch prompts in the background after initial images are loaded
  useEffect(() => {
    if (twoDImages && twoDImages.length > 0 && !promptsLoading) {
      // Extract AI image render_ids that don't have prompts yet
      const aiImageIds = twoDImages
        .filter(img => (img.image_type === 'ai') && img.id && !promptsData[img.id])
        .map(img => img.id);

      if (aiImageIds.length > 0) {
        setPromptsLoading(true);
        fetchAIImagePrompts(aiImageIds)
          .then(prompts => {
            setPromptsData(prev => ({ ...prev, ...prompts }));
            setPromptsLoading(false);
          })
          .catch(err => {
            console.error('Failed to fetch prompts:', err);
            setPromptsLoading(false);
          });
      }
    }
  }, [twoDImages, fetchAIImagePrompts, promptsLoading]);

  // Function to group AI images by parent_id
  const groupAIImagesByParent = (imageList) => {
    if (!imageList || imageList.length === 0) return imageList;

    const groupedImages = [];
    const parentGroups = {};
    const standaloneImages = [];
    const processedImageIds = new Set(); // Track processed images to avoid duplicates

    // Separate images with parent_id and without parent_id
    imageList.forEach(image => {
      if (image.image_type === 'ai' && image.parent_id) {
        if (!parentGroups[image.parent_id]) {
          parentGroups[image.parent_id] = [];
        }
        parentGroups[image.parent_id].push(image);
        processedImageIds.add(image.id);
      } else {
        standaloneImages.push(image);
      }
    });

    Object.keys(parentGroups).forEach(parentId => {
      const groupImages = parentGroups[parentId];

      // Find the parent image (id equals parent_id) and add it to the group
      const parentImage = imageList.find(img =>
        img.image_type === 'ai' && img.id == parentId
      );

      // Add parent image to the group if it exists
      if (parentImage && !groupImages.some(img => img.id == parentImage.id)) {
        groupImages.push(parentImage);
        processedImageIds.add(parentImage.id);
      }

      groupImages.sort((a, b) => b.id - a.id);

      if (groupImages.length > 1) {
        // Mark the first image (latest) as the display image for the stack
        const displayImage = groupImages[0];
        displayImage.isStackDisplay = true;
        displayImage.stackImages = groupImages;
        displayImage.stackCount = groupImages.length;

        groupedImages.push(displayImage);
      } else {
        // Add single image as standalone (not in a stack)
        groupedImages.push(...groupImages);
      }
    });

    // Add standalone images (excluding those that are parents of groups and already processed)
    const parentIds = Object.keys(parentGroups);
    const standaloneFiltered = standaloneImages.filter(image =>
      !parentIds.includes(image.id) && !processedImageIds.has(image.id)
    );
    groupedImages.push(...standaloneFiltered);

    groupedImages.sort((a, b) => {
      const timeA = new Date(a.time_updated || 0).getTime();
      const timeB = new Date(b.time_updated || 0).getTime();
      return timeB - timeA; // Most recent first
    });

    return groupedImages;
  };

  const twoDImagesWithFavoriteFlag = useMemo(() => {
    const favoriteImagesMapping = favoriteImages?.reduce(
      (acc, obj) => {
        // For ai_video, use just entity_id as key since each video can only be favorited once
        const key = obj?.image_type === 'ai_video'
          ? `${obj?.entity_id}`
          : `${obj?.entity_id}_${obj.image_name}`;
        return { ...acc, [key]: obj };
      },
      {}
    );

    console.log("FAV IMAGES MAPPING: ", favoriteImagesMapping);

    const favoriteImagesResult = twoDImages?.map((obj) => {
      let filename;

      if (obj?.image_type === "lifestyle") filename = obj?.filename;
      else if (obj?.image_type === "silo")
        filename = `${obj?.camera_name}.${obj.image_format}`;
      else if (obj.image_type === "spin_360") {
        filename = `${obj?.entity_id}_Frame1.jpg`;
      } else if (obj.image_type === "mp4") {
        filename = `${obj?.entity_id}.mp4`;
      }
      else if (obj.image_type === "ai") {
        filename = obj.image_name
      }
      else if (obj.image_type === "ai_video") {
        filename = obj.id;
      }

      let key = `${obj.entity_id}_${filename}`;
      if (obj.image_type === "ai") {
        key = `${obj.id}_${obj.image_name}`;
      }
      else if (obj.image_type === "ai_video") {
        key = obj.id;
      }

      // Merge prompt data for AI images
      let mergedObj = {
        ...obj,
        favourite: favoriteImagesMapping?.[key],
        favouriteId: favoriteImagesMapping?.[key]?.id,
        isFavourite: favoriteImagesMapping?.[key] ? true : false,
        favoritedOn: favoriteImagesMapping?.[key]?.favorited_on,
      };

      // Add prompt data if available for AI images
      if (obj.image_type === 'ai' && obj.id && promptsData[obj.id]) {
        mergedObj = {
          ...mergedObj,
          prompt_text: promptsData[obj.id].prompt_text,
          image_prompt: promptsData[obj.id].prompt_text,
          search_keywords: promptsData[obj.id].search_keywords,
        };
      }

      return mergedObj;
    });

    return [...(favoriteImagesResult || [])];
  }, [twoDImages, favoriteImages, promptsData]);

  const displayableImages = useMemo(() => {
    // Group AI images by parent_id before returning
    return groupAIImagesByParent(twoDImagesWithFavoriteFlag);
  }, [twoDImagesWithFavoriteFlag]);

  // AI Image Stack State Management Functions
  const enterStackState = (stackImagesArray) => {
    console.log('Opening AI Image Stack with parent_id:', stackImagesArray[0]?.parent_id);
    console.log('Stack images:', stackImagesArray);
    setStackState(true);
    if (stackImagesArray && stackImagesArray.length > 0) {
      setSelectedStackParentId(stackImagesArray[0].parent_id);
      setStackImages(stackImagesArray);
    }
  };

  const leaveStackState = () => {
    setStackState(false);
    setStackImages([]);
    setSelectedStackParentId(null);
  };

  useEffect(() => {
    console.log(
      "ALL IMAGES LAODING: ",
      imagesAllLoading,
      "ALL IMAGES RESULT: ",
      twoDImagesAll
    );
  }, [imagesAllLoading, twoDImagesAll]);

  useEffect(() => {
    getUserProjects();
  }, []);

  useEffect(() => {
    const imagePayload = {
      username: CUSTOMER_USERNAME,
      request_type: 'ai_image_1k',
      action: 'check',
      category: '',
    };
    axios.post(ENVIRONMENT.PAYMENT_GATEWAY, imagePayload).then((res) => {
      setImageRequestStatus(res.data['request_allowed']);
    });
  }, []);

  const loadUserRequestStatus = () => {
    const payload = {
      username: CUSTOMER_USERNAME,
      request_type: 'ai_image_1k',
      action: 'check',
      category: '',
    };
    axios.post(ENVIRONMENT.PAYMENT_GATEWAY, payload).then((res) => {
      setCardStatus(res.data['card_status']);
    });
  };

  const handleRequest4KImage = (obj) => {
    const base_image_id = obj?.render_id || obj?.id;
    if (!base_image_id) return;

    const currentRender = obj?.renders?.find(r => r.render_id === obj.render_id) || obj;
    const maxDimension = Math.max(currentRender?.width ?? 0, currentRender?.height ?? 0);

    if (maxDimension >= 2048) {
        if (imageRequestStatus !== 'allowed' && imageRequestStatus !== 'always_allowed') {
            setCurrentRequestType('ai_image_1k');
            setSubscriptionUpgradeManagerModel(true);
            return;
        }
        message.loading('Requesting upscale image..');
        const request_payload = {
            action: 'generate_4k_image',
            base_image_id,
            username: localStorage.getItem('username'),
            company_id: localStorage.getItem('company_id'),
            scale: 4,
        };
        const gateway_payload = {
            username: localStorage.getItem('username'),
            request_type: 'ai_image_4k',
            action: 'perform',
            request_payload,
            category: '',
            request_id: Date.now().toString(),
        };
        axios.post(ENVIRONMENT.PAYMENT_GATEWAY, gateway_payload)
            .then(() => {
                message.success('4K image submitted successfully!');
                if (refetch) refetch();
            })
            .catch((error) => {
                message.error('Error submitting 4K image');
                console.error(error);
            });
    } else {
        setUpscaleRenderId(base_image_id);
        setUpscaleModalOpen(true);
    }
  };

  // Polling for in-progress AI videos
  useEffect(() => {
    if (!twoDImagesAll) return;

    const hasInProgress = twoDImagesAll.some((img) => {
      if (img.image_type === "ai_video" && (img.status === "inprogress" || img.status === "in-progress")) {
        const timeUpdated = moment.utc(img.submitted_on || 0);
        const now = moment.utc();
        const diff = now.diff(timeUpdated);
        return diff < 900000; // Only poll if requested in last 15 mins (UTC comparison)
      }
      return false
    });

    if (hasInProgress) {
      const intervalId = setInterval(() => {
        twoDAllImagesRefetch();
      }, 20000); // Poll every 20 seconds

      return () => {
        clearInterval(intervalId);
      }
    }
  }, [twoDImagesAll, twoDAllImagesRefetch]);

  // Refetch images: first check after 10 seconds, then every 20 seconds for up to 4 attempts if new_image_id is present
  useEffect(() => {
    const url = new URL(window.location.href);
    const newImageId = url.searchParams.get('new_image_id');

    if (!newImageId) return; // Exit if no new_image_id in params

    let attempts = 0;
    const maxAttempts = 4;
    let timeoutId = null;

    const checkImage = async () => {
      attempts++;
      console.log(`Checking for image (attempt ${attempts}/${maxAttempts})...`);

      // Fetch data silently without triggering re-renders using direct API call
      try {
        const result = await twoDImagesFetcher(allImagesPayload);

        // Check if the new image exists in the fetched data
        const imageExists = result?.some(img => img.id == newImageId && img.status == 'completed');
        console.log("****", imageExists);

        if (imageExists) {
          message.success("Image request successful");

          // Now refetch to update the UI with the completed image
          await twoDAllImagesRefetch();
          await favoriteImagesRefetch();

          // Remove the URL param
          url.searchParams.delete('new_image_id');
          url.searchParams.delete('image_src');
          window.history.pushState({}, '', url);

          // Update state to hide the temporary loader
          setShowNewImageLoader(null);
        } else if (attempts >= maxAttempts) {
          // After 4 attempts (70 seconds total), show error
          message.error("Image request failed");
          setShowNewImageLoader(null);

          // Remove the URL param
          url.searchParams.delete('new_image_id');
          url.searchParams.delete('image_src');
          window.history.pushState({}, '', url);
        } else {
          // Schedule next check in 20 seconds
          timeoutId = setTimeout(checkImage, 20000);
        }
      } catch (error) {
        console.error('Error checking for new image:', error);
        // On error, still schedule next attempt if we haven't exceeded max attempts
        if (attempts < maxAttempts) {
          timeoutId = setTimeout(checkImage, 20000);
        }
      }
    };

    // Start first check after 10 seconds
    timeoutId = setTimeout(checkImage, 10000);

    // Cleanup function to clear timeout if component unmounts
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []); // Empty dependency array ensures this runs only once

  useEffect(() => {
    const url = new URL(window.location.href);
    const upscale_reqest_status = url.searchParams.get('upscale_request_status');

    if (upscale_reqest_status == 'completed') {
      message.success("Upscale image request successful");

      // Remove the parameter from URL after showing the message
      url.searchParams.delete('upscale_request_status');
      window.history.replaceState({}, '', url.toString());
    }
  }, [])

  const markFavorite = (obj) => {
    console.log("MARK OBJECT: ", obj);

    const payload = {
      action: "add_favorite",
      entity_id: obj.image_type === "ai" ? obj.id : obj.entity_id,
      entity_type: obj.entity_type,
      image_type: obj.image_type,
      favorited_by: CUSTOMER_USERNAME,
    };

    if (obj?.image_type === "silo") {
      payload["image_name"] = `${obj.camera_name}.${obj.image_format}`;
    } else if (obj?.image_type === "lifestyle") {
      payload["image_name"] = obj?.filename;
    } else if (obj?.image_type === "mp4") {
      payload["image_name"] = obj?.entity_id + ".mp4";
    } else if (obj?.image_type === "spin_360") {
      payload["image_name"] = obj?.entity_id + "_Frame1.jpg";
    } else if (obj?.image_type === "ai") {
      payload["image_name"] = obj?.image_name;
    } else if (obj?.image_type === "ai_video") {
      payload["image_name"] = obj?.display_name || obj?.filename || obj?.image_name;
      payload["entity_id"] = obj.id || obj.render_id;
    }
    axios.post(ENVIRONMENT.FAVOURITES_CONTROLLER, payload).then((res) => {
      console.log("RES: ", res);

      favoriteImagesRefetch(); //commenting it to avoid rerender as we do local update, todo: on faliure update local isFav of image
    });
  };

  const unmarkFavorite = (obj) => {
    console.log("MARK OBJECT: ", obj);

    const payload = {
      action: "remove_favorite",
      favorite_id: obj?.favouriteId,
      favorited_by: CUSTOMER_USERNAME,
    };
    axios.post(ENVIRONMENT.FAVOURITES_CONTROLLER, payload).then((res) => {
      console.log("RES: ", res);
      favoriteImagesRefetch(); //commenting it to avoid rerender as we do local update, todo: on faliure update local isFav of image
    });
  };

  const GetImageURL = (item, thumbnail = false) => {
    if (item.image_type == "silo") {
      if (
        item.image_status == "completed" &&
        (!("is_hidden" in item) || item.is_hidden !== true)
      ) {
        let silo_base_uri = "";
        let unique_attributer = "username";
        if (item.company_id) {
          silo_base_uri = "company/" + item.company_id;
          unique_attributer = "company";
        } else {
          silo_base_uri = item.username;
        }

        let imageUrl =
          ENVIRONMENT.getBaseURL(item["platform"]) +
          (thumbnail
            ? ENVIRONMENT.LOW_RES_QA_IMG_URI
            : ENVIRONMENT.QA_IMG_URI) +
          item["entity_id"] +
          "/" +
          silo_base_uri +
          "/" +
          item.camera_name +
          (thumbnail ? "_512" : "") +
          "." +
          item.image_format.replace("tiff", "jpg");
        return imageUrl;
      }
    }

    if (item.image_type === "ai" || item.image_type === "ai_video") {
      if (item.is360) {
        let baseUrl = "";
        if (item.company_id) {
          baseUrl = "company/" + item.company_id;
        } else {
          baseUrl = item.captured_by ? item.captured_by : item.username;
        }

        let imageUrl =
          ENVIRONMENT.getBaseURL() +
          "product_assets/ai_generated_spin/" +
          `${item.product_id}/frames/` +
          item.uri;
        return imageUrl;
      }
      if (thumbnail && item.image_type === "ai_video") {
        return ENVIRONMENT.getBaseURL(item.platform) + item.thumbnail;

      }
      if (thumbnail && item.uri) {
        return ENVIRONMENT.getBaseURL(item.platform) + item.uri.replace(/(\.[^.]+)$/, "_512$1"); // add _512 before the file format to get low res url link
      }
      if (thumbnail && item.thumbnail) {
        return ENVIRONMENT.getBaseURL(item.platform) + item.thumbnail;
      }
      return ENVIRONMENT.getBaseURL(item.platform) + item.uri;
    }

    if (item.image_type == "lifestyle") {
      if (thumbnail) {
        const parts = item.filename.split(".");
        const updatedFilename =
          parts.slice(0, -1).join(".") + "_512." + parts.at(-1);
        return (
          ENVIRONMENT.getBaseURL(item.platform) +
          ENVIRONMENT.SCENE_THUMBNAIL_URI +
          item.entity_id +
          "/" +
          updatedFilename
        ).replace("tiff", "jpg");
      } else {
        return (
          ENVIRONMENT.getBaseURL(item.platform) +
          ENVIRONMENT.SCENE_THUMBNAIL_URI +
          item.entity_id +
          "/" +
          item.filename
        ).replace("tiff", "jpg");
      }
    }

    if (item.image_type == "spin_360") {
      let baseUrl = "";
      if (item.company_id) {
        baseUrl = "company/" + item.company_id;
      } else {
        baseUrl = item.captured_by ? item.captured_by : item.customer_username;
      }

      let imageUrl =
        ENVIRONMENT.getBaseURL() +
        "product_assets/threesixtys_frames/" +
        `${baseUrl}/` +
        item.spin_360_frames[0];
      return imageUrl;
    }

    if (item.image_type == "mp4") {
      let baseUrl = "";
      if (item.company_id) {
        baseUrl = "company/" + item.company_id;
      } else {
        baseUrl = item.captured_by ? item.captured_by : item.customer_username;
      }

      let imageUrl =
        ENVIRONMENT.getBaseURL() +
        "product_assets/video/" +
        `${baseUrl}/` +
        item.entity_id +
        ".mp4";
      return imageUrl;
    }
  };

  const setImageType = (e) => {
    setType(e);
    console.log(e);
  };

  const getResolutionCategory = (image) => {
    if (image.height && image.width) {
      const maxDimension = Math.max(image.height, image.width);

      if (maxDimension <= 1024) {
        return "1k";
      } else if (maxDimension <= 2048) {
        return "2k";
      } else if (maxDimension <= 4096) {
        return "4k";
      } else {
        return "custom";
      }
    } else if (image.resolution) {
      if (image.resolution <= 1024) {
        return "1k";
      } else if (image.resolution <= 2048) {
        return "2k";
      } else if (image.resolution <= 4096) {
        return "4k";
      } else {
        return "custom";
      }
    } else if (
      image.spin_360_details &&
      image.spin_360_details.frame_resolution
    ) {
      if (parseInt(image.spin_360_details.frame_resolution) == 1) {
        return "1k";
      } else if (parseInt(image.spin_360_details.frame_resolution) == 2) {
        return "2k";
      } else if (parseInt(image.spin_360_details.frame_resolution) == 4) {
        return "4k";
      } else {
        return "custom";
      }
    }
  };

  const onChangeResolution = (e) => {
    const newResolution = e.target.value;
    setResolution(newResolution);
    // Get current URL parameters
    const params = new URLSearchParams(window.location.search);

    // Set the new resolution in the query parameters
    params.set("resolution", newResolution);

    // Update the URL without reloading
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params}`
    );
  };

  const onChangeTabResolution = (tabName, newResolution) => {
    const updatedTabResolutions = {
      ...tabResolutions,
      [tabName]: newResolution
    };
    setTabResolutions(updatedTabResolutions);

    // Get current URL parameters
    const params = new URLSearchParams(window.location.search);

    // Set the tab resolutions in the query parameters
    params.set("tabResolutions", JSON.stringify(updatedTabResolutions));

    // Update the URL without reloading
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params}`
    );
  };

  const getTabResolution = (tabName) => {
    return tabResolutions[tabName] || "all";
  };

  // Get distinct usernames from AI images
  const distinctUsernames = useMemo(() => {
    if (!twoDImagesWithFavoriteFlag) return [];
    const usernames = new Set();
    twoDImagesWithFavoriteFlag.forEach((image) => {
      if (image.image_type === 'ai' && image.username) {
        usernames.add(image.username);
      }
    });
    return Array.from(usernames).sort();
  }, [twoDImagesWithFavoriteFlag]);

  const getUserProjects = () => {
    //console.log('fgsf')
    let payload = {
      order_by: "customer_username",
      required_fields: ["id", "name", "customer_username"],
      filter_string: `(status__notexact='approved')&&(username__exact='${CUSTOMER_USERNAME}')&&(access_level__in=['owner','co_owner','editor'])`,
    };

    axios.post(ENVIRONMENT.GET_PROJECT_BATCH, payload).then((res) => {
      let projects = [];
      if (MANAGED_CUSTOMER_USERNAME) {
        projects = res.data.map((item) => ({
          ...item,
          name: `[${item.customer_username}] ${item.name} `,
        }));

        setProjects(projects);
      } else {
        setProjects(res.data);
      }
    });
    //console.log(projects);
  };

  const handleCollaborateAiImage = async (obj) => {
    console.log('Collaborate on image:', obj);

    let prompt_text = obj?.flux_prompt || obj?.prompt_text;
    const toBase64Url = (uint8) => {
      let binary = '';
      for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
      return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    };
    const compressState = (stateObj) => {
      const json = JSON.stringify(stateObj || {});
      const deflated = pako.deflate(json);
      return toBase64Url(deflated);
    };
    const state = {
      img: obj?.input_image_uri || "",
      imgInspection: obj?.inspiration_image_uri || "",
      inspiration_id: obj?.inspiration_id || null,
      prompt: prompt_text || "",
      props: obj?.props || [],
    };
    const collaborateParams = new URLSearchParams();
    collaborateParams.set("ai_image", "true");
    collaborateParams.set("individual_collab", "true");
    collaborateParams.set("productId", obj.entity_id);
    if (obj?.job_id) collaborateParams.set("job_id", String(obj.job_id));
    if (obj?.inspiration_id != null) collaborateParams.set("inspiration_id", String(obj.inspiration_id));
    collaborateParams.set("state", compressState(state));

    if (obj.individual_collab_id) {
      window.location.href = `/collaborate-tool/${obj.individual_collab_id}?${collaborateParams.toString()}`;
    }
    else {
      message.loading('Collaborating on image...');
      const userName = localStorage.getItem('username')

      const payload = {
        image_id: obj.id,
        display_name: obj.display_name || "Untitled",
        camera_name: obj.uri,
        thumbnail: obj.uri.replace(/(\.[^.]+)$/, "_512$1"),
        created_by: userName,
        last_modified_by: userName,
        render_path: obj.uri,
        state: {}
      }

      console.log('payload', payload)

      const res = await axios.post(ENVIRONMENT.CREATE_COLLABORATE_ENTITY, payload)

      console.log('res', res)
      window.location.href = `/collaborate-tool/${res.data.body.collaboration_entity_id}?${collaborateParams.toString()}`;
      message.success('Collaboration created successfully');
    }
  }

  const handleSubmitForReview = (obj) => {

    console.log('render', obj);
    message.loading('Submitting for review...');

    const payload = {
        action: "add_review_image",
        submitted_by: CUSTOMER_USERNAME,
        product_id: obj.entity_id
    };

    if(obj?.image_type == 'ai_video'){
      payload['video_id'] = obj.id;
      payload['action'] = "add_review_video"
    }
    else{
      payload['image_id'] = obj.id;
    }
    console.log("*****", payload)
    axios.post(ENVIRONMENT.REVIEW_IMAGE_CONTROLLER, payload)
      .then((res) => {
        message.success("Image submitted for review successfully!");
      })
      .catch((error) => {
        message.error("Error submitting image for review");
        console.error(error);
      });
  }

  return (
    <CustomerMainLayout selected="25">
      <TwoDImagesContext.Provider
        value={{
          resolution: resolution,
          setResolution: setResolution,
          getResolutionCategory: getResolutionCategory,
          projects: projects,
          setProjects: setProjects,
          Type: Type,
          setImageType: setImageType,
          onChangeResolution: onChangeResolution,
          formState: formState,
          setFormState: setFormState,
          images: twoDImagesWithFavoriteFlag,
          GetImageURL: GetImageURL,
          markFavorite: markFavorite,
          unmarkFavorite: unmarkFavorite,
          imagesInitialLoading: imagesAllLoading,
          refetch: refetch,
          // New tab-specific resolution functions
          tabResolutions: tabResolutions,
          onChangeTabResolution: onChangeTabResolution,
          getTabResolution: getTabResolution,
          // AI Image Stack State Management
          stackState: stackState,
          stackImages: stackImages,
          selectedStackParentId: selectedStackParentId,
          enterStackState: enterStackState,
          leaveStackState: leaveStackState,
          groupAIImagesByParent: groupAIImagesByParent,
          // New image loader state
          showNewImageLoader: showNewImageLoader,
          newImageSrc: newImageSrc,
          // Distinct usernames for filtering
          distinctUsernames: distinctUsernames,
          // Prompts loading state
          promptsLoading: promptsLoading,
          handleCollaborateAiImage: handleCollaborateAiImage,
          handleSubmitForReview: handleSubmitForReview,
          setSubscriptionUpgradeManagerModel: setSubscriptionUpgradeManagerModel,
          setCurrentRequestType: setCurrentRequestType,
          handleRequest4KImage: handleRequest4KImage,
          // Completed bulk requests for filtering
          completedBulkRequests: completedBulkRequests,
          // Pagination state
          paginationState: paginationState,
          favouritesCount: favouritesCount,
        }}
      >
        {!stackState && <TwoDImagesHeader />}
        <TwoDImagesTabs />
      </TwoDImagesContext.Provider>
      <SubscriptionUpgradeManager
        subscriptionUpgradeManagerModel={subscriptionUpgradeManagerModel}
        setSubscriptionUpgradeManagerModel={setSubscriptionUpgradeManagerModel}
        cardStatus={cardStatus}
        loadUserRequestStatus={loadUserRequestStatus}
        requestType={currentRequestType}
      />
      <UpscaleImageModal
        isOpen={upscaleModalOpen}
        onClose={() => setUpscaleModalOpen(false)}
        renderId={upscaleRenderId}
        onSuccess={() => { if (refetch) refetch(); }}
      />
    </CustomerMainLayout>
  );
};

const mapStateToProps = (state) => state;
const mapDispatchToProps = () => ({});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TwoDImagesPage)
);
